import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchCardCoordinatesAsync, fetchCardCoordinatesBatchAsync } from './RodLiftAnalysisService';

interface CardCoordinate {
  x: number;
  y: number;
}

export interface CardTrend {
  id: number;
  name: string;
  coordinates: CardCoordinate[];
}

export interface Card {
  cardDate: string;
  cardTrends: CardTrend[];
}

interface Cards {
  [cardDate: string]: Card;
}

interface CardCoordinatesState {
  values: Cards;
  assetId: string;
  isLoading: boolean;
  pendingBatchCount: number; // Track how many batch operations are pending
  isBatchOperation: boolean; // Track if we're in the middle of batch operations
}

// Define batch response interface
interface CardResponse {
  cardDate: string;
  values: {
    id: number;
    name: string;
    coordinatesOutput: {
      x: number;
      y: number;
    }[];
  }[];
}

interface BatchCardResponse {
  cards: CardResponse[];
  id: string;
  dateCreated: string;
}

const fetchRodLiftAnalysisCardCoordinatesAsync = createAsyncThunk(
  'RodLiftAnalysis/fetchCardCoordinates',
  async (input: { assetId: string; cardDate: string }) => {
    const response = await fetchCardCoordinatesAsync(input.assetId, input.cardDate);

    return response;
  },
);

const fetchRodLiftAnalysisCardCoordinatesBatchAsync = createAsyncThunk(
  'RodLiftAnalysis/fetchCardCoordinatesBatch',
  async (input: { assetId: string; cardDates: string[] }) => {
    const response = await fetchCardCoordinatesBatchAsync(input.assetId, input.cardDates);
    return response as BatchCardResponse;
  },
);

const initialState: CardCoordinatesState = {
  values: {},
  assetId: '',
  isLoading: false,
  pendingBatchCount: 0,
  isBatchOperation: false,
};

const rodLiftAnalysisCardCoordinatesSlice = createSlice({
  name: 'RodLiftAnalysis/CardCoordinatesSlice',
  initialState,
  reducers: {
    startBatchOperation: (state, action) => {
      state.isBatchOperation = true;
      state.pendingBatchCount = action.payload.totalBatches;
      state.isLoading = true;
    },
    resetBatchState: (state) => {
      state.isBatchOperation = false;
      state.pendingBatchCount = 0;
    },
    addSingleCardCoordinates: (state, action) => {
      const card = action.payload as Card;
      state.values[card.cardDate] = card;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRodLiftAnalysisCardCoordinatesAsync.pending, (state, action) => {
      // If fetching data for new asset id then clear slice state.
      const assetId = action.meta.arg.assetId;
      const existingAssetId = state.assetId;

      if (assetId != existingAssetId) {
        return { ...initialState, isLoading: true };
      }
      return;
    });
    builder.addCase(fetchRodLiftAnalysisCardCoordinatesAsync.fulfilled, (state, action) => {
      const cardDate = action.meta.arg.cardDate;
      const assetId = action.meta.arg.assetId;

      const card: Card = {
        cardDate: cardDate,
        cardTrends:
          action.payload?.values?.map((x) => {
            return {
              id: x.id,
              name: x.name,
              coordinates: x.coordinates,
            } as CardTrend;
          }) ?? [],
      };
      state.values[cardDate] = card;
      state.assetId = assetId;
      state.isLoading = false;
    });
    builder.addCase(fetchRodLiftAnalysisCardCoordinatesAsync.rejected, (state, action) => {
      state.isLoading = false;
      console.error(action.error.message);
    });
    builder.addCase(fetchRodLiftAnalysisCardCoordinatesBatchAsync.pending, (state, action) => {
      // If fetching data for new asset id then clear slice state.
      const assetId = action.meta.arg.assetId;
      const existingAssetId = state.assetId;

      if (assetId != existingAssetId) {
        return { ...initialState, isLoading: true, isBatchOperation: true, pendingBatchCount: 1 };
      }

      // If not in batch operation, this is a single batch request
      if (!state.isBatchOperation) {
        state.isLoading = true;
      }

      return state;
    });
    builder.addCase(fetchRodLiftAnalysisCardCoordinatesBatchAsync.fulfilled, (state, action) => {
      const assetId = action.meta.arg.assetId;
      state.assetId = assetId;

      // Handle batch completion tracking
      if (state.isBatchOperation) {
        state.pendingBatchCount--;

        // If this was the last batch, complete the batch operation
        if (state.pendingBatchCount <= 0) {
          state.isLoading = false;
          state.isBatchOperation = false;
          state.pendingBatchCount = 0;
        }
      } else {
        // Single batch operation
        state.isLoading = false;
      }

      // Process batch response
      if (action.payload && action.payload.cards) {
        action.payload.cards.forEach((cardData) => {
          const cardDate = cardData.cardDate;
          const cardTrends = cardData.values.map(
            (x) =>
              ({
                id: x.id,
                name: x.name,
                coordinates:
                  x.coordinatesOutput?.map((coord) => ({
                    x: coord.x,
                    y: coord.y,
                  })) || [],
              }) as CardTrend,
          );

          state.values[cardDate] = {
            cardDate,
            cardTrends,
          };
        });
      }
    });
    builder.addCase(fetchRodLiftAnalysisCardCoordinatesBatchAsync.rejected, (state, action) => {
      // Handle batch rejection
      if (state.isBatchOperation) {
        state.pendingBatchCount--;

        // If this was the last batch (even if failed), complete the batch operation
        if (state.pendingBatchCount <= 0) {
          state.isLoading = false;
          state.isBatchOperation = false;
          state.pendingBatchCount = 0;
        }
      } else {
        state.isLoading = false;
      }

      console.error(action.error.message);
    });
  },
});

export { fetchRodLiftAnalysisCardCoordinatesAsync, fetchRodLiftAnalysisCardCoordinatesBatchAsync };
export const { startBatchOperation, resetBatchState, addSingleCardCoordinates } =
  rodLiftAnalysisCardCoordinatesSlice.actions;
export default rodLiftAnalysisCardCoordinatesSlice.reducer;
